import { useState, useRef, useEffect, useMemo } from 'react';
import { MapPin, ChevronDown, ChevronRight } from 'lucide-react';
import {
  swedishCounties,
  getAllCounties,
  getLocationsForCounty,
  getCountyDisplayName
} from '@/data/swedish-counties';

interface RegionMultiSelectProps {
  selected: string[];
  onChange: (regions: string[]) => void;
  language?: 'en' | 'sv';
}

type Tab = 'locations' | 'counties';

export function RegionMultiSelect({ selected, onChange, language = 'sv' }: RegionMultiSelectProps) {
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState<Tab>('locations');
  const [expandedCounty, setExpandedCounty] = useState<string | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);

  const counties = useMemo(() => getAllCounties(), []);
  const allLocations = useMemo(
    () =>
      counties
        .flatMap((c) => [c, ...swedishCounties[c].municipalities])
        .sort((a, b) => a.localeCompare(b, 'sv')),
    [counties]
  );

  const selectedCounties = useMemo(() => selected.filter((s) => s in swedishCounties), [selected]);

  useEffect(() => {
    if (!open) return;
    function onClick(e: MouseEvent) {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  function toggleLocation(loc: string) {
    onChange(selected.includes(loc) ? selected.filter((r) => r !== loc) : [...selected, loc]);
  }

  function toggleCounty(countyName: string) {
    const countyLocations = getLocationsForCounty(countyName);
    const isSelected = selected.includes(countyName);
    if (isSelected) {
      onChange(selected.filter((r) => r !== countyName && !countyLocations.includes(r)));
    } else {
      const next = new Set(selected);
      next.add(countyName);
      countyLocations.forEach((l) => next.add(l));
      onChange([...next]);
    }
  }

  const label =
    selected.length === 0
      ? language === 'sv'
        ? 'Välj regioner'
        : 'Select regions'
      : selectedCounties.length === 1 && selected.length <= selectedCounties.length + 1
        ? getCountyDisplayName(selectedCounties[0], language)
        : `${selected.length} ${language === 'sv' ? 'valda' : 'selected'}`;

  return (
    <div className="relative" ref={rootRef}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex h-9 min-w-[180px] items-center justify-between gap-2 rounded-md border bg-background px-3 text-sm outline-none hover:border-primary/60 focus:ring-1 focus:ring-ring"
      >
        <span className="flex items-center gap-1.5 truncate">
          <MapPin className="h-3.5 w-3.5 text-muted-foreground" />
          <span className={selected.length === 0 ? 'text-muted-foreground' : ''}>{label}</span>
        </span>
        <ChevronDown
          className={`h-3.5 w-3.5 text-muted-foreground transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </button>

      {open && (
        <div className="absolute left-0 top-full z-50 mt-1 w-80 max-h-96 overflow-hidden rounded-md border bg-popover text-popover-foreground shadow-md">
          <div className="sticky top-0 flex gap-1 border-b bg-popover p-1">
            <button
              type="button"
              onClick={() => setTab('locations')}
              className={`flex-1 rounded px-2 py-1 text-xs font-medium ${
                tab === 'locations' ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'
              }`}
            >
              {language === 'sv' ? 'Platser' : 'Locations'}
            </button>
            <button
              type="button"
              onClick={() => setTab('counties')}
              className={`flex-1 rounded px-2 py-1 text-xs font-medium ${
                tab === 'counties' ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'
              }`}
            >
              {language === 'sv' ? 'Län' : 'Counties'}
            </button>
          </div>

          <div className="max-h-80 overflow-y-auto p-1">
            {tab === 'locations' &&
              allLocations.map((loc) => (
                <label
                  key={loc}
                  className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-xs hover:bg-muted"
                >
                  <input
                    type="checkbox"
                    checked={selected.includes(loc)}
                    onChange={() => toggleLocation(loc)}
                    className="h-3.5 w-3.5"
                  />
                  {loc}
                </label>
              ))}

            {tab === 'counties' &&
              counties.map((countyName) => {
                const county = swedishCounties[countyName];
                const isExpanded = expandedCounty === countyName;
                const isSelected = selected.includes(countyName);
                return (
                  <div key={countyName} className="rounded border-b border-border/50 last:border-0">
                    <div
                      className={`flex items-center gap-2 px-2 py-1.5 text-xs ${
                        isSelected ? 'bg-primary/10' : ''
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggleCounty(countyName)}
                        className="h-3.5 w-3.5 cursor-pointer"
                      />
                      <span
                        className="flex-1 cursor-pointer"
                        onClick={() => toggleCounty(countyName)}
                      >
                        <span className="font-medium">
                          {getCountyDisplayName(countyName, language)}
                        </span>
                        <span className="ml-2 text-[10px] text-muted-foreground">
                          {county.municipalities.length}{' '}
                          {language === 'sv' ? 'kommuner' : 'municipalities'}
                        </span>
                      </span>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setExpandedCounty(isExpanded ? null : countyName);
                        }}
                        className="rounded p-0.5 hover:bg-muted"
                      >
                        <ChevronRight
                          className={`h-3 w-3 transition-transform ${isExpanded ? 'rotate-90' : ''}`}
                        />
                      </button>
                    </div>
                    {isExpanded && (
                      <div className="bg-muted/40 p-2">
                        <div className="flex flex-wrap gap-1">
                          {county.municipalities.map((m) => (
                            <span
                              key={m}
                              className="rounded border bg-background px-1.5 py-0.5 text-[10px]"
                            >
                              {m}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
          </div>
        </div>
      )}
    </div>
  );
}

interface RegionChipsProps {
  selected: string[];
  onChange: (regions: string[]) => void;
  language?: 'en' | 'sv';
}

export function RegionChips({ selected, onChange, language = 'sv' }: RegionChipsProps) {
  if (selected.length === 0) return null;

  function clearRegion(region: string) {
    if (region in swedishCounties) {
      const locs = getLocationsForCounty(region);
      onChange(selected.filter((r) => r !== region && !locs.includes(r)));
    } else {
      onChange(selected.filter((r) => r !== region));
    }
  }

  return (
    <div className="flex flex-wrap gap-1">
      {selected.map((region) => {
        const isCounty = region in swedishCounties;
        return (
          <span
            key={region}
            className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-medium ${
              isCounty ? 'bg-primary/15 text-primary' : 'bg-muted text-muted-foreground'
            }`}
          >
            {isCounty ? getCountyDisplayName(region, language) : region}
            <button
              type="button"
              onClick={() => clearRegion(region)}
              className="hover:opacity-70"
              aria-label={`Remove ${region}`}
            >
              ×
            </button>
          </span>
        );
      })}
      <button
        type="button"
        onClick={() => onChange([])}
        className="text-[10px] text-muted-foreground underline hover:text-foreground"
      >
        {language === 'sv' ? 'Rensa' : 'Clear all'}
      </button>
    </div>
  );
}
